import { useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DynamicForm from '@/components/school-management/Form/DynamicForm/DynamicForm';
import { getEditTeacherFormConfig } from '@/school-management/config/formConfig';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { teacherService } from '@/school-management/services';
import { trimNamePart } from '@/utils/personName';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';

export default function EditTeacherFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { teacher } = location.state || {};
  const { teacherId, schoolId } = useParams();
  const { user } = useAppSelector(authSelector);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useAdminTranslation();

  const initialValues = useMemo(() => {
    if (!teacher) return undefined;
    return {
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email || `${teacher.firstName}.${teacher.lastName}@example.com`,
    } as Record<string, any>;
  }, [teacher]);

  const formConfig = useMemo(() => {
    const config = getEditTeacherFormConfig(t);
    config.fields = config.fields.map(c => {
      if (c.name === 'save' && isLoading) {
        return { ...c, loading: true };
      }
      return c;
    });
    return config;
  }, [t, isLoading]);

  return (
    <>
      <DynamicForm
        config={{
          ...formConfig,
          onSubmit: async data => {
            const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
            if (!organizationId || !teacherId) {
              navigate(-1);
              return;
            }

            setIsLoading(true);

            try {
              const resp = await teacherService.updateTeacher(organizationId, teacherId, {
                firstName: trimNamePart(data.firstName),
                lastName: trimNamePart(data.lastName),
              });
              const updatedFirst = resp?.data?.data?.updatedFields?.firstName;
              const updatedLast = resp?.data?.data?.updatedFields?.lastName;
              const isSuccess = resp?.data?.status === 'success' && updatedFirst && updatedLast;
              if (isSuccess) {
                // Wait 2 seconds for DynamoDB stream to update postgres
                await new Promise(resolve => setTimeout(resolve, 2000));

                navigate(`/schools/${schoolId}?tab=teachers`, {
                  replace: true,
                  state: { notification: { message: t('dynamicForm.notification.teacherEdited') } },
                });
              } else {
                navigate(-1);
              }
            } catch (e) {
              navigate(-1);
            }

            try {
              setIsLoading(false);
            } catch (e) {}
          },
        }}
        initialValues={initialValues}
        showPopupOnSubmit={false}
        isreturn={true}
        showPopupOk={false}
        //TODO: change to the correct tab
        onCancel={() => navigate(`/schools/${schoolId}?tab=teachers`)}
      />
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type='success'
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
    </>
  );
}
